import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doctorsApi, filesApi } from '@/api';
import type { DoctorDetail } from '@/api';
import { Button, Card, Badge, Skeleton, cn } from '@medikei/ui';
import { BookAppointmentModal } from '@/components/scheduling';
import { Header } from '@/landing/v3/sections/Header';
import { useAuth } from '@/hooks';
import { favoritesApi } from '@/api/favorites-api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Star,
  GraduationCap,
  Briefcase,
  Languages,
  ShieldCheck,
  Stethoscope,
  Users,
  Video,
  Building2,
  Home,
  FileCheck,
  User,
  Heart,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function getDoctorInitials(name?: string | null): string {
  if (!name) return 'MD';
  const cleaned = name.replace(/^(dr\.|dra\.|dr|dra)\s+/i, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (!first) return 'MD';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] || ''}${last?.[0] || ''}`.toUpperCase();
}

function getAgeRangeLabel(doctor: DoctorDetail): string {
  const minAge = doctor.minAge !== undefined && doctor.minAge !== null ? Number(doctor.minAge) : null;
  const maxAge = doctor.maxAge !== undefined && doctor.maxAge !== null ? Number(doctor.maxAge) : null;
  const isPediatria = Boolean(
    doctor.primarySpecialty?.name?.toLowerCase().includes('pediat') ||
    doctor.allowedAgeGroups?.some((g) => g.toLowerCase().includes('pediat'))
  );

  // Se for especialista em Pediatria ou maxAge <= 17: exibir Atende: Crianças e Adolescentes (0 a 17 anos) (ou até o maxAge real)
  if (isPediatria || (maxAge !== null && maxAge <= 17)) {
    const endAge = maxAge !== null && maxAge > 0 ? maxAge : 17;
    return `Atende: Crianças e Adolescentes (0 a ${endAge} anos)`;
  }

  // Se minAge === 0 && maxAge >= 100: exibir Atende: Todas as idades
  if (minAge === 0 && maxAge !== null && maxAge >= 100) {
    return 'Atende: Todas as idades';
  }

  // Se minAge >= 18: exibir Atende: Adultos e Idosos (18+)
  if (minAge !== null && minAge >= 18) {
    return 'Atende: Adultos e Idosos (18+)';
  }

  // Caso contrário: Atende: ${minAge} a ${maxAge} anos
  if (minAge !== null && maxAge !== null) {
    return `Atende: ${minAge} a ${maxAge} anos`;
  }

  if (minAge !== null) {
    return `Atende: A partir de ${minAge} anos`;
  }

  if (maxAge !== null) {
    return `Atende: Até ${maxAge} anos`;
  }

  if (doctor.allowedAgeGroups && doctor.allowedAgeGroups.length > 0) {
    const formatted = doctor.allowedAgeGroups.map((g) => {
      const upper = g.toUpperCase();
      if (upper.includes('PED') || upper.includes('INFANT') || upper.includes('KID') || upper.includes('CRIAN')) return 'Crianças';
      if (upper.includes('TEEN') || upper.includes('ADOL')) return 'Adolescentes';
      if (upper.includes('ADULT')) return 'Adultos';
      if (upper.includes('ELDER') || upper.includes('IDOS')) return 'Idosos';
      return g;
    });
    return `Atende: ${formatted.join(', ')}`;
  }

  return 'Atende: Todas as idades';
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

export function DoctorProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [doctor, setDoctor] = useState<DoctorDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Navegação inteligente de volta respeitando o papel e histórico
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (user?.role === 'DOCTOR') {
      navigate('/medico/dashboard');
    } else if (user?.role === 'PATIENT') {
      navigate('/dashboard');
    } else {
      navigate('/buscar');
    }
  };

  const backLabel = user?.role === 'DOCTOR' ? 'Voltar ao painel médico' : 'Voltar à busca';

  // Checagem de favorito na visão do paciente
  const { data: isFavData } = useQuery({
    queryKey: ['patient', 'favorite-check', doctor?.id],
    queryFn: () => (doctor?.id ? favoritesApi.isFavorite(doctor.id) : false),
    enabled: Boolean(user?.role === 'PATIENT' && doctor?.id),
  });

  const isFavorite = Boolean(isFavData);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async () => {
      if (!doctor?.id) return;
      if (!user) {
        navigate('/login');
        return;
      }
      if (isFavorite) {
        await favoritesApi.removeFavorite(doctor.id);
      } else {
        await favoritesApi.addFavorite(doctor.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', 'favorites'] });
      queryClient.invalidateQueries({ queryKey: ['patient', 'favorite-check', doctor?.id] });
    },
  });

  useEffect(() => {
    const loadDoctor = async () => {
      if (!slug) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await doctorsApi.getDoctorBySlug(slug);
        setDoctor(data);
        setImageError(false);
      } catch {
        setError('Médico não encontrado');
      } finally {
        setIsLoading(false);
      }
    };
    loadDoctor();
  }, [slug]);

  const renderStars = (rating: number, totalRatings: number = 0) => {
    return (
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`h-4 w-4 ${
                star <= Math.round(rating)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-muted-foreground/30'
              }`}
            />
          ))}
        </div>
        <span className="text-sm font-bold text-foreground">
          {rating.toFixed(1)}
        </span>
        {totalRatings > 0 && (
          <span className="text-xs text-muted-foreground">
            ({totalRatings} {totalRatings === 1 ? 'avaliação' : 'avaliações'})
          </span>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Header />
        <div className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
          <Skeleton className="h-9 w-36 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card rounded-2xl border border-border p-6">
                <div className="flex flex-col sm:flex-row gap-6 items-start">
                  <Skeleton className="h-20 w-20 sm:h-28 sm:w-28 rounded-2xl sm:rounded-3xl shrink-0" />
                  <div className="flex-1 w-full space-y-3">
                    <Skeleton className="h-8 w-3/4 max-w-sm" />
                    <Skeleton className="h-5 w-48" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-6 w-28" />
                    </div>
                  </div>
                </div>
              </div>
              <Skeleton className="h-48 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
            <div className="lg:col-span-1">
              <Skeleton className="h-80 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Header />
        <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-8 text-center flex flex-col items-center justify-center">
          <Stethoscope className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Médico não encontrado</h2>
          <p className="text-muted-foreground mb-6">
            O médico que você está procurando não foi encontrado.
          </p>
          <Button onClick={() => navigate('/buscar')}>Voltar à busca</Button>
        </div>
      </div>
    );
  }

  const rawPhoto = doctor.profile?.photoUrl;
  const photoUrl = rawPhoto ? filesApi.getFileUrl(rawPhoto) : null;
  const hasPhoto = Boolean(photoUrl && !imageError);

  const rawCover = (doctor.profile as any)?.coverUrl || (doctor as any)?.coverUrl;
  const coverUrl = rawCover
    ? rawCover.startsWith('http') || rawCover.startsWith('data:')
      ? rawCover
      : filesApi.getFileUrl(rawCover)
    : null;

  const displayName = doctor.profile?.displayName || 'Médico';
  const initials = getDoctorInitials(displayName);

  const priceValue = doctor.startingPrice ?? (doctor.consultationPrice != null
    ? (doctor.consultationPrice >= 1000 ? doctor.consultationPrice / 100 : doctor.consultationPrice)
    : 120);
  const formattedPrice = formatCurrency(priceValue);

  const hasEducationOrExperience = Boolean(
    (doctor.education && doctor.education.length > 0) ||
    (doctor.experiences && doctor.experiences.length > 0)
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header />
      <div className="flex-1 max-w-6xl w-full mx-auto px-4 py-8 pb-28 lg:pb-12">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-6 hover:bg-muted font-semibold rounded-xl text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Médico Responsivo & Moderno */}
            <div className="bg-card rounded-3xl border border-border/80 dark:border-white/[0.08] shadow-xl dark:shadow-[0_20px_50px_rgba(0,0,0,0.5),inset_0_1px_0_0_rgba(255,255,255,0.08)] relative overflow-hidden">
              {/* Imagem de Capa Panorâmica */}
              {coverUrl ? (
                <div className="relative w-full h-44 sm:h-56 overflow-hidden bg-muted/60">
                  <img
                    src={coverUrl}
                    alt={`Capa de ${displayName}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                </div>
              ) : (
                <div className="relative w-full h-32 sm:h-36 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/5">
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
                </div>
              )}

              {/* Backlight Glow */}
              <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-primary/10 rounded-full blur-3xl pointer-events-none -z-0" />

              <div className="relative z-10 px-6 sm:px-8 pb-6 sm:pb-7 -mt-14 sm:-mt-20">
                <div className="flex flex-col sm:flex-row gap-5 items-start">
                  {/* Avatar em moldura moderna com status online */}
                  <div className="relative shrink-0">
                    <div className="h-28 w-28 sm:h-36 sm:w-36 rounded-3xl ring-4 ring-card shadow-xl overflow-hidden bg-gradient-to-br from-primary/25 via-primary/10 to-primary/5 flex items-center justify-center">
                      {hasPhoto ? (
                        <img
                          src={photoUrl!}
                          alt={displayName}
                          onError={() => setImageError(true)}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-primary font-bold text-3xl sm:text-5xl select-none">
                          {initials}
                        </span>
                      )}
                    </div>
                    {/* Status Indicator */}
                    <span
                      className="absolute -bottom-1 -right-1 sm:bottom-1 sm:right-1 flex h-4 w-4"
                      title="Disponível para atendimento"
                    >
                      <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-4 w-4 rounded-full bg-emerald-500 border-2 border-card shadow-xs" />
                    </span>
                  </div>

                  {/* Informações Principais */}
                  <div className="flex-1 min-w-0 sm:pt-14">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground break-words">
                        {displayName}
                      </h1>

                    {/* Botão de Favoritar no Hero */}
                    <button
                      type="button"
                      onClick={() => toggleFavoriteMutation.mutate()}
                      disabled={toggleFavoriteMutation.isPending}
                      className={cn(
                        'px-3.5 py-1.5 rounded-xl border transition-all duration-150 flex items-center gap-1.5 cursor-pointer shadow-xs',
                        isFavorite
                          ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
                          : 'bg-muted/40 border-border/80 text-muted-foreground hover:text-red-500 hover:bg-red-500/10 hover:border-red-500/20'
                      )}
                      title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Heart
                        className={cn(
                          'w-4 h-4 transition-transform active:scale-125',
                          isFavorite && 'fill-red-500 text-red-500'
                        )}
                      />
                      <span className="text-xs font-bold">
                        {isFavorite ? 'Favorito' : 'Favoritar'}
                      </span>
                    </button>
                  </div>

                  <p className="text-base sm:text-lg font-bold text-primary mb-3.5 break-words">
                    {doctor.primarySpecialty?.name || 'Clínica Geral'}
                  </p>

                  {/* Badges e Informações */}
                  <div className="flex flex-wrap items-center gap-2 mb-3.5">
                    <Badge
                      variant="outline"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 rounded-xl"
                    >
                      <ShieldCheck className="h-4 w-4 shrink-0" />
                      CRM {doctor.crm} - {doctor.crmState} (Verificado)
                    </Badge>

                    {doctor.experience && (
                      <Badge
                        variant="outline"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-muted-foreground border-border/80 bg-muted/30 rounded-xl"
                      >
                        <Briefcase className="h-4 w-4 shrink-0" />
                        {doctor.experience} anos de experiência
                      </Badge>
                    )}

                    <Badge
                      variant="outline"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-primary/10 text-primary border-primary/20 rounded-xl"
                    >
                      <Users className="h-4 w-4 shrink-0" />
                      {getAgeRangeLabel(doctor)}
                    </Badge>
                  </div>

                  {doctor.statistics && (
                    <div className="mb-2.5">
                      {renderStars(doctor.statistics.averageRating, doctor.statistics.totalRatings)}
                    </div>
                  )}

                    {doctor.profile?.tagline && (
                      <p className="mt-2 text-sm text-muted-foreground italic leading-relaxed bg-muted/20 p-3 rounded-xl border border-border/40">
                        "{doctor.profile.tagline}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Card: Sobre o Especialista */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">Sobre o Especialista</h2>
              </div>
              {doctor.bio ? (
                <div className="text-muted-foreground text-sm sm:text-base leading-relaxed whitespace-pre-line">
                  {doctor.bio}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm sm:text-base italic">
                  Profissional de saúde comprometido com atendimento humanizado, precisão no diagnóstico e cuidado integral aos pacientes na plataforma Medikei.
                </p>
              )}

              {doctor.profile?.languages && doctor.profile.languages.length > 0 && (
                <div className="mt-5 pt-4 border-t border-border/60">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2">
                    Idiomas de atendimento
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {doctor.profile.languages.map((lang) => (
                      <Badge key={lang} variant="outline" className="inline-flex items-center gap-1">
                        <Languages className="h-3 w-3" />
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Card: Especialidades e Áreas de Atuação */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Stethoscope className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-bold">Especialidades e Áreas de Atuação</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1.5">
                    Especialidade Principal
                  </span>
                  <p className="text-base font-semibold text-foreground">
                    {doctor.primarySpecialty?.name || 'Clínica Geral'}
                  </p>
                </div>

                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1.5">
                    Faixa Etária Atendida
                  </span>
                  <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-xs py-1 px-2.5">
                    <Users className="h-3.5 w-3.5 mr-1" />
                    {getAgeRangeLabel(doctor)}
                  </Badge>
                </div>

                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2">
                    Modalidades de Atendimento
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 py-1 px-2.5">
                      <Video className="h-3.5 w-3.5 mr-1.5" />
                      Telemedicina
                    </Badge>
                    {doctor.hasInPerson && (
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 py-1 px-2.5">
                        <Building2 className="h-3.5 w-3.5 mr-1.5" />
                        Consulta Presencial
                      </Badge>
                    )}
                    {doctor.hasHomeCare && (
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 py-1 px-2.5">
                        <Home className="h-3.5 w-3.5 mr-1.5" />
                        Atendimento Domiciliar
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Card: Formação e Experiência (apenas se existirem dados) */}
            {hasEducationOrExperience && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-bold">Formação e Experiência</h2>
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  {doctor.education && doctor.education.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <GraduationCap className="h-4 w-4" /> Formação Acadêmica
                      </h3>
                      <div className="space-y-3">
                        {doctor.education.map((edu) => (
                          <div key={edu.id} className="border-l-2 border-primary/40 pl-3.5 py-0.5">
                            <p className="font-semibold text-sm text-foreground">{edu.degree}</p>
                            <p className="text-xs text-muted-foreground">{edu.institution}</p>
                            {edu.field && (
                              <p className="text-xs text-muted-foreground/80 mt-0.5">{edu.field}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {doctor.experiences && doctor.experiences.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <Briefcase className="h-4 w-4" /> Trajetória Profissional
                      </h3>
                      <div className="space-y-3">
                        {doctor.experiences.map((exp) => (
                          <div key={exp.id} className="border-l-2 border-primary/40 pl-3.5 py-0.5">
                            <p className="font-semibold text-sm text-foreground">{exp.title}</p>
                            <p className="text-xs text-muted-foreground">{exp.company}</p>
                            <p className="text-[11px] text-muted-foreground/75 mt-0.5">
                              {format(new Date(exp.startDate), 'MMM yyyy', { locale: ptBR })} -{' '}
                              {exp.endDate
                                ? format(new Date(exp.endDate), 'MMM yyyy', { locale: ptBR })
                                : 'Atual'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Card: Avaliações dos Pacientes */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-400 fill-amber-400" />
                  <h2 className="text-lg font-bold">Avaliações dos Pacientes</h2>
                </div>
                {doctor.statistics && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= Math.round(doctor.statistics?.averageRating || 0)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="font-bold text-sm">
                      {doctor.statistics.averageRating.toFixed(1)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({doctor.statistics.totalRatings} avaliações)
                    </span>
                  </div>
                )}
              </div>

              {doctor.ratings && doctor.ratings.length > 0 ? (
                <div className="space-y-4">
                  {doctor.ratings.map((rating) => (
                    <div key={rating.id} className="border-b border-border/60 pb-4 last:border-b-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">
                              {rating.patient?.user?.id?.charAt(0).toUpperCase() || 'P'}
                            </span>
                          </div>
                          <span className="text-sm font-medium">Paciente Verificado</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-3.5 w-3.5 ${
                                star <= rating.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-muted-foreground/30'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {rating.comment && (
                        <p className="text-sm text-muted-foreground leading-relaxed">{rating.comment}</p>
                      )}
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {format(new Date(rating.createdAt), "dd 'de' MMMM 'de' yyyy", {
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 border border-dashed border-border rounded-xl">
                  <Star className="h-8 w-8 text-amber-400/40 mx-auto mb-2" />
                  <p className="text-sm font-medium text-foreground">Excelente histórico profissional</p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                    As avaliações de consultas concluídas através da plataforma serão exibidas aqui com verificação de autenticidade.
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Desktop Conversion Sticky Box */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              <Card className="p-6 border-2 border-primary/20 shadow-lg bg-card">
                {/* Preço em destaque */}
                <div className="pb-4 border-b border-border/80">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                    Consulta a partir de
                  </span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-black text-foreground tracking-tight">
                      {formattedPrice}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">/ atendimento</span>
                  </div>
                </div>

                {/* Modalidades disponíveis com valores vinculados */}
                <div className="py-4 border-b border-border/80 space-y-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                    Modalidades &amp; Valores
                  </span>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-xs font-medium text-foreground">
                      <div className="flex items-center gap-2.5">
                        <div className="h-6 w-6 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                          <Video className="h-3.5 w-3.5" />
                        </div>
                        <div>
                          <span>Telemedicina</span>
                          <span className="text-muted-foreground block text-[11px]">Vídeo chamada integrada</span>
                        </div>
                      </div>
                      <span className="font-bold text-foreground">
                        {formatCurrency(doctor.telemedicinePrice ?? priceValue)}
                      </span>
                    </div>

                    {doctor.hasInPerson && (
                      <div className="flex items-center justify-between text-xs font-medium text-foreground">
                        <div className="flex items-center gap-2.5">
                          <div className="h-6 w-6 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                            <Building2 className="h-3.5 w-3.5" />
                          </div>
                          <div>
                            <span>Presencial</span>
                            <span className="text-muted-foreground block text-[11px]">No consultório médico</span>
                          </div>
                        </div>
                        <span className="font-bold text-foreground">
                          {formatCurrency(doctor.inPersonPrice ?? 180)}
                        </span>
                      </div>
                    )}

                    {doctor.hasHomeCare && (
                      <div className="flex items-center justify-between text-xs font-medium text-foreground">
                        <div className="flex items-center gap-2.5">
                          <div className="h-6 w-6 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                            <Home className="h-3.5 w-3.5" />
                          </div>
                          <div>
                            <span>Domiciliar</span>
                            <span className="text-muted-foreground block text-[11px]">No conforto da sua casa</span>
                          </div>
                        </div>
                        <span className="font-bold text-foreground">
                          {formatCurrency(doctor.homeCarePrice ?? 250)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botão de agendamento principal com Favoritar */}
                <div className="pt-4 space-y-4">
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => toggleFavoriteMutation.mutate()}
                      disabled={toggleFavoriteMutation.isPending}
                      className={cn(
                        'h-12 w-12 rounded-xl border flex items-center justify-center transition-all cursor-pointer shrink-0 shadow-xs',
                        isFavorite
                          ? 'bg-red-500/10 border-red-500/30 text-red-500'
                          : 'bg-card border-border hover:bg-muted text-muted-foreground hover:text-red-500'
                      )}
                      title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Heart
                        className={cn(
                          'w-5 h-5 transition-transform active:scale-125',
                          isFavorite && 'fill-red-500 text-red-500'
                        )}
                      />
                    </button>

                    <Button
                      size="lg"
                      className="flex-1 text-base font-bold shadow-lg shadow-primary/25 hover:shadow-xl hover:-translate-y-0.5 transition-all h-12 rounded-xl"
                      onClick={() => setIsBookingOpen(true)}
                    >
                      Agendar consulta
                    </Button>
                  </div>

                  {/* Selos de segurança */}
                  <div className="space-y-2 pt-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span>Ambiente seguro e criptografado</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileCheck className="h-4 w-4 text-primary shrink-0" />
                      <span>Receita digital válida CFM em todo Brasil</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Booking Bar */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-md border-t border-border z-40 flex items-center justify-between gap-3 lg:hidden shadow-lg">
          <div>
            <span className="text-[11px] text-muted-foreground uppercase font-semibold block leading-none mb-1">
              Consulta
            </span>
            <span className="text-xl font-bold text-foreground tracking-tight">
              {formattedPrice}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => toggleFavoriteMutation.mutate()}
              disabled={toggleFavoriteMutation.isPending}
              className={cn(
                'h-11 w-11 rounded-xl border flex items-center justify-center transition-all cursor-pointer shrink-0 shadow-xs',
                isFavorite
                  ? 'bg-red-500/10 border-red-500/30 text-red-500'
                  : 'bg-card border-border hover:bg-muted text-muted-foreground hover:text-red-500'
              )}
              title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            >
              <Heart
                className={cn('w-5 h-5', isFavorite && 'fill-red-500 text-red-500')}
              />
            </button>

            <Button
              size="md"
              className="font-bold px-6 shadow-lg shadow-primary/25 rounded-xl h-11"
              onClick={() => setIsBookingOpen(true)}
            >
              Agendar consulta
            </Button>
          </div>
        </div>

        {doctor && (
          <BookAppointmentModal
            isOpen={isBookingOpen}
            onClose={() => setIsBookingOpen(false)}
            doctor={doctor}
          />
        )}
      </div>
    </div>
  );
}
